import { Outlet, createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import DispensaryAccountScreen from "./pages/DispensaryAccountScreen";
import Dashboard from "./pages/Dashboard";
import { DispensaryOnboardingScreen } from "./pages/DispensaryOnboardingScreen";
import AdminCreateDispensaryScreen from "./pages/AdminCreateDispensaryScreen";
import { InvoicesScreen } from "./pages/InvoicesScreen";
import InventoryPlaceholderScreen from "./pages/InventoryPlaceholderScreen";
import { OrdersQueueScreen } from "./pages/OrdersQueueScreen";
import { RunsActiveScreen } from "./pages/RunsActiveScreen";
import SupportScreen from "./pages/SupportScreen";
import { WorkspaceLayout } from "./workspace/WorkspaceLayout";

const rootRoute = createRootRoute({
  component: function RootLayout() {
    return (
      <div className="min-h-screen bg-bg">
        <Outlet />
        {import.meta.env.DEV ? <TanStackRouterDevtools position="bottom-left" /> : null}
      </div>
    );
  },
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/workspace/dashboard" });
  },
});

const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workspace",
  component: WorkspaceLayout,
});

const workspaceIndexRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/workspace/dashboard" });
  },
});

const workspaceDashboardRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "dashboard",
  validateSearch: (raw: Record<string, unknown>): { q?: string } => {
    const q = raw.q;
    if (typeof q !== "string" || q.trim().length === 0) return {};
    return { q: q.trim() };
  },
  component: Dashboard,
});

const workspaceOrdersRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "orders",
  component: OrdersQueueScreen,
});

const workspaceRunsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "runs",
  component: RunsActiveScreen,
});

const workspaceInventoryRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "inventory",
  component: InventoryPlaceholderScreen,
});

const workspaceInvoicesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "invoices",
  component: InvoicesScreen,
});

const workspaceSettlementsRedirectRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "settlements",
  beforeLoad: () => {
    throw redirect({ to: "/workspace/invoices" });
  },
});

const workspaceAccountRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "account",
  component: DispensaryAccountScreen,
});

const workspaceSupportRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "support",
  component: SupportScreen,
});

const workspaceAdminCreateDispensaryRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "admin/create-dispensary",
  component: AdminCreateDispensaryScreen,
});

const workspaceOnboardingRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "onboarding",
  component: DispensaryOnboardingScreen,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  workspaceRoute.addChildren([
    workspaceIndexRoute,
    workspaceDashboardRoute,
    workspaceOrdersRoute,
    workspaceInventoryRoute,
    workspaceRunsRoute,
    workspaceInvoicesRoute,
    workspaceSettlementsRedirectRoute,
    workspaceOnboardingRoute,
    workspaceAccountRoute,
    workspaceSupportRoute,
    workspaceAdminCreateDispensaryRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
